// src/components/CaseView/PostCallRecap.tsx
// ──────────────────────────────────────────────────────────────
// Right-rail slide panel: assistant that closes out the call.
// Non-blocking, interruptible, state-preserving.
// Target: < 20 seconds, feels like a brief aside not a form.
// ──────────────────────────────────────────────────────────────

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { supabase } from "../../lib/supabase";

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

export interface CallRecapData {
  case_id: string;
  outcome: string;
  reviewer_id?: string;
  call_reference_number?: string;
  reviewer_stated_specialty: string;
  reviewer_specialty?: string;
  reviewer_had_authority: string;
  criteria_referenced: string;
  call_duration: string;
  call_experience: string[];
  next_step: string;
  notes?: string;
}

interface PostCallRecapProps {
  caseId: string;
  insuranceCompany?: string;
  reviewerId?: string;
  callReferenceNumber?: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface Interpretation {
  pattern: string;
  action: string;
  actionId: string;
  confidence: "high" | "moderate" | "low";
}

// ═══════════════════════════════════════════════════════════════
// OPTION CONFIGS
// ═══════════════════════════════════════════════════════════════

const OUTCOMES = [
  { id: "approved", label: "Approved", icon: "\u2713", accent: "emerald" },
  { id: "denied", label: "Denied", icon: "\u2715", accent: "red" },
  { id: "partially_approved", label: "Partial", icon: "\u25d0", accent: "amber" },
  { id: "deferred", label: "Deferred", icon: "\u2192", accent: "slate" },
  { id: "couldnt_decide", label: "No Decision", icon: "?", accent: "slate" },
] as const;

const OUTCOME_CONFIRMATION: Record<string, string> = {
  approved: "Got it \u2014 documenting approval.",
  denied: "Got it \u2014 building your next step.",
  partially_approved: "Got it \u2014 recording partial approval.",
  deferred: "Got it \u2014 tracking the deferral.",
  couldnt_decide: "Got it \u2014 documenting for escalation.",
};

const REVIEWER_APPROPRIATENESS = [
  { id: "yes", label: "Yes" },
  { id: "probably", label: "Probably" },
  { id: "no", label: "No" },
  { id: "not_sure", label: "Not sure" },
] as const;

const ESCALATION = [
  { id: "no", label: "No" },
  { id: "yes", label: "Yes" },
  { id: "not_sure", label: "Not sure" },
] as const;

const CRITERIA_TYPE = [
  { id: "named_criteria", label: "Named criteria" },
  { id: "general_statements", label: "General only" },
  { id: "wouldnt_specify", label: "Wouldn\u2019t say" },
] as const;

const SPECIFIC_CRITERIA = [
  { id: "interqual", label: "InterQual" },
  { id: "mcg", label: "MCG" },
  { id: "cms", label: "CMS guidance" },
  { id: "plan_specific", label: "Plan policy" },
  { id: "clinical_guidelines", label: "Clinical guidelines" },
  { id: "other_criteria", label: "Other" },
] as const;

const DURATION_OPTIONS = [
  { id: "under_5", label: "< 5 min" },
  { id: "5_to_10", label: "5\u201310" },
  { id: "10_to_20", label: "10\u201320" },
  { id: "over_20", label: "20+" },
] as const;

const CALL_CHARACTERISTICS = [
  { id: "long_hold", label: "Long wait" },
  { id: "scripted", label: "Scripted" },
  { id: "unfamiliar", label: "Unfamiliar" },
  { id: "rushed", label: "Rushed" },
  { id: "disconnected", label: "Disconnected" },
  { id: "none", label: "None" },
] as const;

const NEXT_STEP_OPTIONS = [
  { id: "accepting", label: "Accept decision" },
  { id: "internal_appeal", label: "Internal appeal" },
  { id: "expedited_external", label: "Expedited external review" },
  { id: "both_appeals", label: "Both simultaneously" },
  { id: "network_adequacy", label: "Network adequacy exception" },
  { id: "undecided", label: "Decide later" },
] as const;

const SPECIALTIES = [
  "Allergy/Immunology", "Anesthesiology", "Cardiology", "Dermatology",
  "Emergency Medicine", "Endocrinology", "Family Medicine", "Gastroenterology",
  "General Surgery", "Geriatrics", "Hematology/Oncology", "Infectious Disease",
  "Internal Medicine", "Nephrology", "Neurology", "Neurosurgery", "OB/GYN",
  "Ophthalmology", "Orthopedic Surgery", "Otolaryngology (ENT)", "Pain Medicine",
  "Pathology", "Pediatrics", "Physical Medicine & Rehab", "Plastic Surgery",
  "Psychiatry", "Pulmonology", "Radiology", "Rheumatology", "Thoracic Surgery",
  "Urology", "Vascular Surgery", "Other", "Don\u2019t know",
] as const;

// ═══════════════════════════════════════════════════════════════
// INTERPRETATION ENGINE
// ═══════════════════════════════════════════════════════════════

function generateInterpretation(
  outcome: string,
  reviewer: string,
  escalation: string,
  criteriaType: string,
  specificCriteria: string,
  characteristics: Set<string>,
  duration: string,
): Interpretation {
  const isScripted = characteristics.has("scripted");
  const wasEscalated = escalation === "yes";
  const noCriteria = criteriaType === "wouldnt_specify" || criteriaType === "general_statements";
  const notAppropriate = reviewer === "no";
  const shortCall = duration === "under_5";

  let pattern: string;
  let action: string;
  let actionId: string;
  let confidence: Interpretation["confidence"];

  switch (outcome) {
    case "approved":
      pattern = "Authorization obtained.";
      action = "No further action needed. Approval documented.";
      actionId = "accepting";
      confidence = "high";
      break;

    case "denied": {
      if ((isScripted || shortCall) && (wasEscalated || notAppropriate)) {
        pattern = "Call pattern suggests a procedural denial rather than clinical review. Short duration with limited engagement is associated with higher overturn rates on external review.";
        action = "File expedited external review. The procedural pattern strengthens the case for independent review.";
        actionId = "expedited_external";
        confidence = "high";
      } else if (noCriteria && !notAppropriate) {
        pattern = "Denial issued without specific criteria citation. This is a potential procedural deficiency that can be raised in appeal.";
        action = "Request written denial with specific criteria. File internal appeal citing lack of clinical basis.";
        actionId = "internal_appeal";
        confidence = "moderate";
      } else if (notAppropriate) {
        pattern = "Reviewer may not have had appropriate clinical background for this case type. Same-specialty review requirements may apply.";
        action = "File internal appeal requesting same-specialty review. Consider simultaneous external review.";
        actionId = "both_appeals";
        confidence = "high";
      } else if (criteriaType === "named_criteria" && reviewer !== "no") {
        pattern = "Substantive clinical disagreement with a specific criteria set. Appeal should directly address the cited criteria.";
        action = "File internal appeal targeting the cited criteria. Strengthen argument with supporting guidelines.";
        actionId = "internal_appeal";
        confidence = "moderate";
      } else if (wasEscalated) {
        pattern = "Reviewer needed to escalate the decision, suggesting the case exceeded standard review parameters.";
        action = "Follow up on escalation timeline. Prepare additional documentation for the next-level reviewer.";
        actionId = "undecided";
        confidence = "moderate";
      } else {
        pattern = "Standard denial. Review the written denial letter for specific criteria to target in appeal.";
        action = "File internal appeal within plan deadline. Consider simultaneous external review if timeline is urgent.";
        actionId = "internal_appeal";
        confidence = "moderate";
      }
      break;
    }

    case "partially_approved":
      pattern = "Partial authorization \u2014 some clinical basis accepted. Appeal should target the denied portion specifically.";
      action = "Appeal the denied portion with targeted clinical justification.";
      actionId = "internal_appeal";
      confidence = "moderate";
      break;

    case "deferred":
      if (wasEscalated) {
        pattern = "Reviewer escalated to committee, suggesting the case exceeded standard review parameters.";
        action = "Document the deferral and follow up within the stated timeline. Prepare additional materials for committee.";
        actionId = "undecided";
        confidence = "moderate";
      } else {
        pattern = "Decision deferred to committee review.";
        action = "Follow up within 48 hours if no timeline was stated. Prepare written supporting materials.";
        actionId = "undecided";
        confidence = "low";
      }
      break;

    case "couldnt_decide":
      pattern = "Review ended without a determination.";
      action = "Send written follow-up requesting determination within 24\u201348 hours. Escalate if no response.";
      actionId = "undecided";
      confidence = "moderate";
      break;

    default:
      pattern = "Call outcome recorded.";
      action = "Review denial letter and determine next step.";
      actionId = "undecided";
      confidence = "low";
  }

  return { pattern, action, actionId, confidence };
}

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

/** Inline single-select chips */
function Chips({
  options, value, onChange,
}: {
  options: readonly { id: string; label: string }[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(value === opt.id ? "" : opt.id)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 border whitespace-nowrap ${
            value === opt.id
              ? "bg-teal-500/20 border-teal-500/50 text-teal-300"
              : "bg-slate-800/40 border-slate-700/40 text-slate-500 hover:border-slate-600 hover:text-slate-400"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** Inline multi-select chips */
function MultiChips({
  options, values, onChange,
}: {
  options: readonly { id: string; label: string }[];
  values: Set<string>;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 border whitespace-nowrap ${
            values.has(opt.id)
              ? "bg-teal-500/20 border-teal-500/50 text-teal-300"
              : "bg-slate-800/40 border-slate-700/40 text-slate-500 hover:border-slate-600 hover:text-slate-400"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** Outcome buttons — larger, colored accents */
function OutcomeSelector({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {OUTCOMES.map((opt) => {
        const isSelected = value === opt.id;
        const accentMap: Record<string, string> = {
          emerald: isSelected ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300" : "",
          red: isSelected ? "bg-red-500/20 border-red-500/50 text-red-300" : "",
          amber: isSelected ? "bg-amber-500/20 border-amber-500/50 text-amber-300" : "",
          slate: isSelected ? "bg-teal-500/20 border-teal-500/50 text-teal-300" : "",
        };
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={`px-2.5 py-3 rounded-lg text-sm font-medium transition-all duration-150 border ${
              isSelected
                ? accentMap[opt.accent]
                : "bg-slate-800/50 border-slate-700/50 text-slate-400 hover:border-slate-600 hover:text-slate-300"
            }`}
          >
            <span className="flex items-center gap-1.5 justify-center">
              <span className="text-base leading-none">{opt.icon}</span>
              {opt.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SpecialtyPills({ value, onChange }: { value: string; onChange: (s: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1">
      {SPECIALTIES.map((spec) => (
        <button
          key={spec}
          type="button"
          onClick={() => onChange(value === spec ? "" : spec)}
          className={`px-2 py-1 rounded-full text-[11px] font-medium transition-all duration-150 border whitespace-nowrap ${
            value === spec
              ? "bg-teal-500/20 border-teal-500/50 text-teal-300"
              : "bg-slate-800/30 border-slate-700/30 text-slate-500 hover:border-slate-600 hover:text-slate-400"
          }`}
        >
          {spec}
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export function PostCallRecap({
  caseId,
  insuranceCompany,
  reviewerId,
  callReferenceNumber,
  isOpen,
  onClose,
  onComplete,
}: PostCallRecapProps) {
  // ── Form state (preserved across open/close) ──
  const [outcome, setOutcome] = useState("");
  const [reviewerAppropriate, setReviewerAppropriate] = useState("");
  const [showSpecialty, setShowSpecialty] = useState(false);
  const [reviewerSpecialty, setReviewerSpecialty] = useState("");
  const [escalation, setEscalation] = useState("");
  const [criteriaType, setCriteriaType] = useState("");
  const [specificCriteria, setSpecificCriteria] = useState("");
  const [callDuration, setCallDuration] = useState("");
  const [callCharacteristics, setCallCharacteristics] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState("");

  // ── Submission state ──
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [chosenAction, setChosenAction] = useState("");
  const [showAltActions, setShowAltActions] = useState(false);

  // ── Animation state ──
  // Track if we've ever been opened (to avoid rendering before first open)
  const [hasOpened, setHasOpened] = useState(false);
  // Controls the CSS transition target
  const [visible, setVisible] = useState(false);

  // ── Refs ──
  const panelRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Manage slide animation
  useEffect(() => {
    if (isOpen) {
      setHasOpened(true);
      // Double-rAF so the browser paints the off-screen state first
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  // Click outside panel to close
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
      onClose();
    }
  }, [onClose]);

  // Scroll to result after submission
  useEffect(() => {
    if (submitted && resultRef.current) {
      const t = setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return () => clearTimeout(t);
    }
  }, [submitted]);

  // ── Interpretation ──
  const interpretation = useMemo(() =>
    generateInterpretation(
      outcome, reviewerAppropriate, escalation,
      criteriaType, specificCriteria, callCharacteristics, callDuration,
    ),
    [outcome, reviewerAppropriate, escalation, criteriaType, specificCriteria, callCharacteristics, callDuration]
  );

  // ── Handlers ──
  const handleCharacteristicToggle = useCallback((id: string) => {
    setCallCharacteristics((prev) => {
      const next = new Set(prev);
      if (id === "none") {
        return next.has("none") ? new Set() : new Set(["none"]);
      }
      next.delete("none");
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }, []);

  const handleCriteriaTypeChange = useCallback((id: string) => {
    setCriteriaType(id);
    if (id !== "named_criteria") setSpecificCriteria("");
  }, []);

  // CTA pulse — briefly animate when outcome changes
  const [ctaPulse, setCtaPulse] = useState(false);
  const handleOutcomeChange = useCallback((id: string) => {
    setOutcome(id);
    setCtaPulse(true);
    setTimeout(() => setCtaPulse(false), 600);
  }, []);

  const canSubmit = outcome !== "";

  const handleSubmit = async () => {
    if (!canSubmit || submitting || submitted) return;
    setSubmitting(true);

    const effectiveNextStep = chosenAction || interpretation.actionId;
    const recap: CallRecapData = {
      case_id: caseId,
      outcome,
      reviewer_id: reviewerId?.trim() || undefined,
      call_reference_number: callReferenceNumber?.trim() || undefined,
      reviewer_stated_specialty: reviewerAppropriate || "skipped",
      reviewer_specialty: reviewerSpecialty || undefined,
      reviewer_had_authority: escalation === "yes" ? "required_escalation"
        : escalation === "no" ? "decision_live"
        : escalation === "not_sure" ? "unclear"
        : "skipped",
      criteria_referenced: criteriaType === "named_criteria" && specificCriteria
        ? specificCriteria
        : criteriaType || "skipped",
      call_duration: callDuration || "skipped",
      call_experience: callCharacteristics.size > 0 ? Array.from(callCharacteristics) : ["skipped"],
      next_step: effectiveNextStep || "skipped",
      notes: notes.trim() || undefined,
    };

    try {
      const { error } = await supabase.from("call_recaps").insert({
        case_id: recap.case_id,
        outcome: recap.outcome,
        reviewer_id: recap.reviewer_id || null,
        reviewer_stated_specialty: recap.reviewer_stated_specialty,
        reviewer_specialty: recap.reviewer_specialty || null,
        reviewer_had_authority: recap.reviewer_had_authority,
        criteria_referenced: recap.criteria_referenced,
        call_duration: recap.call_duration,
        call_experience: recap.call_experience,
        next_step: recap.next_step,
        notes: recap.notes || null,
      });
      if (error) console.error("Failed to save call recap:", error);
    } catch (err) {
      console.error("Error saving recap:", err);
    }

    setSubmitting(false);
    setSubmitted(true);
  };

  const handleProceed = useCallback(() => {
    onComplete();
  }, [onComplete]);

  const handleChooseAction = useCallback((id: string) => {
    setChosenAction(id);
  }, []);

  const confidenceColor = {
    high: "text-emerald-400",
    moderate: "text-amber-400",
    low: "text-slate-400",
  }[interpretation.confidence];

  const confidenceLabel = {
    high: "High confidence",
    moderate: "Moderate confidence",
    low: "Assess further",
  }[interpretation.confidence];

  // Don't render anything until first open
  if (!hasOpened) return null;

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    // Backdrop — light dim, click to close, no scroll blocking
    <div
      className={`fixed inset-0 z-40 transition-colors duration-300 ${
        visible ? "bg-black/10" : "bg-transparent"
      }`}
      onClick={handleBackdropClick}
      style={{ pointerEvents: isOpen ? "auto" : "none" }}
    >
      {/* Panel — slides from right */}
      <div
        ref={panelRef}
        className={`fixed top-0 right-0 bottom-0 w-full max-w-[480px] bg-slate-900 border-l border-slate-800/80 shadow-2xl shadow-black/40 flex flex-col transition-transform duration-300 ease-out ${
          visible ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/60">
          <div className="flex-1 min-w-0">
            <h2 className="text-[15px] font-semibold text-slate-200 leading-snug">
              Call finished {"\u2014"} want me to handle the paperwork?
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {insuranceCompany ? `${insuranceCompany} · ` : ""}A few taps · each recap sharpens your next prep
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 p-1.5 rounded-md text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain">

          {/* ═══ ZONE 1: Outcome (primary) ═══ */}
          <div className="px-5 pt-5 pb-4">
            <p className="text-xs font-medium text-slate-400 mb-3">What happened?</p>
            <OutcomeSelector value={outcome} onChange={handleOutcomeChange} />
            {outcome && (
              <p className="text-xs text-slate-500 mt-2.5 leading-relaxed">
                {OUTCOME_CONFIRMATION[outcome]}
              </p>
            )}
          </div>

          {/* ═══ ZONE 2: What happened (compact tagging) ═══ */}
          {outcome && (
            <div className={`px-5 pb-4 space-y-3.5 transition-opacity duration-300 ${
              submitted ? "opacity-40 pointer-events-none" : "opacity-100"
            }`}>
              {/* Subtle separator */}
              <div className="h-px bg-slate-800/60" />

              {/* Reviewer ID & Reference # — show if captured during call */}
              {(reviewerId || callReferenceNumber) && (
                <div className="flex items-center gap-3 flex-wrap">
                  {reviewerId && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-medium uppercase tracking-wider text-slate-600">Reviewer</span>
                      <span className="text-xs text-slate-300 px-2 py-0.5 rounded bg-slate-800/60 border border-slate-700/30">
                        {reviewerId}
                      </span>
                    </div>
                  )}
                  {callReferenceNumber && (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-medium uppercase tracking-wider text-slate-600">Ref #</span>
                      <span className="text-xs text-slate-300 px-2 py-0.5 rounded bg-slate-800/60 border border-slate-700/30">
                        {callReferenceNumber}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Reviewer appropriateness */}
              <div>
                <p className="text-xs text-slate-500 mb-1.5">Clinically appropriate reviewer?</p>
                <Chips options={REVIEWER_APPROPRIATENESS} value={reviewerAppropriate} onChange={setReviewerAppropriate} />
                {reviewerAppropriate && !showSpecialty && (
                  <button
                    onClick={() => setShowSpecialty(true)}
                    className="text-[11px] text-slate-600 hover:text-slate-400 transition-colors mt-1.5"
                  >
                    + tag specialty
                  </button>
                )}
                {showSpecialty && (
                  <div className="mt-2">
                    <SpecialtyPills value={reviewerSpecialty} onChange={setReviewerSpecialty} />
                  </div>
                )}
              </div>

              {/* Escalation */}
              <div>
                <p className="text-xs text-slate-500 mb-1.5">Needed to escalate?</p>
                <Chips options={ESCALATION} value={escalation} onChange={setEscalation} />
              </div>

              {/* Criteria */}
              <div>
                <p className="text-xs text-slate-500 mb-1.5">Referenced specific criteria?</p>
                <Chips options={CRITERIA_TYPE} value={criteriaType} onChange={handleCriteriaTypeChange} />
                {criteriaType === "named_criteria" && (
                  <div className="mt-2">
                    <p className="text-[11px] text-slate-600 mb-1">Which did it sound most like?</p>
                    <Chips options={SPECIFIC_CRITERIA} value={specificCriteria} onChange={setSpecificCriteria} />
                  </div>
                )}
              </div>

              {/* Duration + characteristics on same visual level */}
              <div className="flex gap-5">
                <div className="flex-shrink-0">
                  <p className="text-xs text-slate-500 mb-1.5">Call time</p>
                  <Chips options={DURATION_OPTIONS} value={callDuration} onChange={setCallDuration} />
                </div>
              </div>

              <div>
                <p className="text-xs text-slate-500 mb-1.5">Describes the call</p>
                <MultiChips options={CALL_CHARACTERISTICS} values={callCharacteristics} onChange={handleCharacteristicToggle} />
              </div>

              {/* Notes — compact */}
              <div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything else worth noting..."
                  rows={1}
                  className="w-full rounded-lg border border-slate-700/30 bg-slate-800/20 text-xs text-slate-400 placeholder-slate-600 px-3 py-2 focus:outline-none focus:border-teal-500/50 focus:ring-1 focus:ring-teal-500/30 resize-none"
                />
              </div>
            </div>
          )}

          {/* ═══ Inline interpretation (after submit) ═══ */}
          {submitted && (
            <div ref={resultRef} className="px-5 pb-5 space-y-3">
              {/* Saved divider */}
              <div className="flex items-center gap-2.5 py-1">
                <div className="h-px flex-1 bg-slate-800/60" />
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded-full bg-teal-500/20 flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Saved</span>
                </div>
                <div className="h-px flex-1 bg-slate-800/60" />
              </div>

              {/* Interpretation */}
              <div className="rounded-lg border border-slate-700/40 bg-slate-800/25 p-3.5">
                <div className="flex items-center gap-1.5 mb-2">
                  <svg className="w-3.5 h-3.5 text-amber-400/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                  </svg>
                  <span className="text-xs font-semibold text-slate-300">Call Interpretation</span>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">{interpretation.pattern}</p>
              </div>

              {/* Recommended action */}
              <div className="rounded-lg border border-teal-500/25 bg-teal-500/5 p-3.5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                    </svg>
                    <span className="text-xs font-semibold text-teal-300">Recommended Next Step</span>
                  </div>
                  <span className={`text-[11px] font-medium ${confidenceColor}`}>{confidenceLabel}</span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed mb-3">{interpretation.action}</p>

                {/* Action buttons */}
                <button
                  onClick={handleProceed}
                  className="w-full py-2.5 rounded-lg text-xs font-semibold bg-teal-500 text-white hover:bg-teal-400 active:bg-teal-600 transition-all duration-200"
                >
                  {chosenAction && chosenAction !== interpretation.actionId
                    ? `Proceed \u2014 ${NEXT_STEP_OPTIONS.find(o => o.id === chosenAction)?.label}`
                    : `Proceed \u2014 ${NEXT_STEP_OPTIONS.find(o => o.id === interpretation.actionId)?.label || "Continue"}`
                  }
                </button>

                {!showAltActions ? (
                  <button
                    onClick={() => setShowAltActions(true)}
                    className="w-full py-1.5 mt-1.5 text-[11px] text-slate-500 hover:text-slate-400 transition-colors"
                  >
                    Choose a different action
                  </button>
                ) : (
                  <div className="mt-2 space-y-1.5">
                    <div className="flex flex-wrap gap-1.5">
                      {NEXT_STEP_OPTIONS.filter(o => o.id !== interpretation.actionId).map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => handleChooseAction(opt.id)}
                          className={`px-2.5 py-1.5 rounded-full text-[11px] font-medium transition-all duration-150 border ${
                            chosenAction === opt.id
                              ? "bg-teal-500/20 border-teal-500/50 text-teal-300"
                              : "bg-slate-800/30 border-slate-700/30 text-slate-500 hover:border-slate-600 hover:text-slate-400"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {chosenAction && chosenAction !== interpretation.actionId && (
                      <button
                        onClick={() => { setChosenAction(""); setShowAltActions(false); }}
                        className="text-[11px] text-slate-600 hover:text-slate-400 transition-colors"
                      >
                        Use recommended instead
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="h-4" />
            </div>
          )}
        </div>

        {/* ── Sticky footer (pre-submission only) ── */}
        {!submitted && (
          <div className="border-t border-slate-800/60 px-5 py-3.5 bg-slate-900/90 backdrop-blur-sm">
            <div className="flex gap-2.5">
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  canSubmit && !submitting
                    ? `bg-teal-500 text-white hover:bg-teal-400 active:bg-teal-600 ${ctaPulse ? "ring-2 ring-teal-400/40 ring-offset-1 ring-offset-slate-900" : ""}`
                    : "bg-slate-800 text-slate-600 cursor-not-allowed"
                }`}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Analyzing...
                  </span>
                ) : (
                  "Analyze & suggest next step"
                )}
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2.5 rounded-lg text-sm text-slate-500 hover:text-slate-400 hover:bg-slate-800/50 transition-colors"
              >
                Skip
              </button>
            </div>
            <p className="text-center text-[11px] text-slate-600 mt-2">
              More detail = better prep next call
            </p>
          </div>
        )}
      </div>
    </div>
  );
}